"use client";

import { ReactNode } from "react";
import { transparentize, mix } from "color2k";
import { useTheme } from "../ThemeProvider";
import "./Elevation.css";

export interface ElevationProps {
  /**
   * The content to be displayed within the elevation.
   */
  children?: ReactNode;
  /**
   * The data-testid attribute for testing purposes.
  */
 "data-testid"?: string;
 /**
  * Whether the elevation should have a background gradient.
  * @important only works in custom dark mode
  * @default false
  */
 hasPrimaryGradient?: boolean;
}

/**
 * Elevation component creates a visual elevation effect using the theme-aware background color.
 *
 * This component is designed to be used as a visual layer that sits on top of other content
 * to create a sense of depth or hierarchy in the UI. It uses the `--proton-control__background-color-light`
 * CSS variable which is theme-aware and will adapt to light, dark themes.
 *
 * API:
 *  - {@link ElevationProps}
 */
export const Elevation = ({
  children,
  hasPrimaryGradient = false,
  "data-testid": dataTestId,
}: ElevationProps) => {
  const { hasPalette, palette, style } = useTheme();

  const defaultBg = style["--proton-control__background-color-light"];
  let primaryDark = defaultBg;
  let secondaryDark = transparentize(defaultBg, 0.01);

  if (hasPalette) {
    primaryDark = transparentize(defaultBg, 0.1);
    secondaryDark = transparentize(defaultBg, 0.12);
    if (hasPrimaryGradient) {
      const basePrimary = transparentize(palette.PRIMARY.MEDIUM_LIGHT, 0.88);
      const baseSecondary = transparentize(palette.PRIMARY.LIGHT, 0.84);
      primaryDark = mix(primaryDark, basePrimary, 0.15);
      secondaryDark = mix(secondaryDark, baseSecondary, 0.15);
    }
  }

  return (
    <div
      className="proton-Elevation"
      data-testid={dataTestId}
      role="presentation"
      aria-label="Elevated content area"
      style={{
        background: `linear-gradient(to top left, ${primaryDark}, ${secondaryDark})`,
      }}
    >
      {children}
    </div>
  );
};
